import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  HomeWork as HomeWorkIcon,
  Sell as SellIcon,
  Build as BuildIcon,
  FormatQuote as FormatQuoteIcon,
} from '@mui/icons-material';

const entryTabs = [
  {
    title: 'Selbstverwaltung (POC)',
    description: 'Kostenloser Einstieg, wenn Sie Ihre Immobilie selbst digital verwalten wollen.',
    cta: 'POC kostenlos starten',
    route: '/leistungen/sondereigentumsverwaltung',
    icon: <BuildIcon />,
  },
  {
    title: 'Hausverwaltung-Service',
    description: 'Wenn Sie operative Verwaltertätigkeiten auslagern möchten, übernehmen wir die Umsetzung.',
    cta: 'Service anfragen',
    route: '/kontakt',
    icon: <HomeWorkIcon />,
  },
  {
    title: 'Immobilien verkaufen',
    description: 'Kaufimmobilien mit Exposé-Workflow und qualifizierter Interessentenansprache.',
    cta: 'Kaufimmobilien finden',
    route: '/immobilien',
    icon: <SellIcon />,
  },
];

const sevHighlights = [
  'Mieteingangskontrolle und Mahnwesen',
  'Mieterkommunikation und Dienstleistersteuerung',
  'Betriebskosten und Kautionsprozesse',
  'Koordination von Instandhaltung im Sondereigentum',
  'Optional Neuvermietung und Übergabeprozesse',
];

// Platzhalter-Referenzen – bei Bedarf durch echte, freigegebene Kundenstimmen ersetzen.
const sevReferences = [
  {
    quote: 'Mieteingangskontrolle und Mahnwesen laufen seit der Übergabe zuverlässig. Ich erhalte klare Monatsreports und muss mich um den operativen Teil nicht mehr kümmern.',
    author: 'Eigentümer einer vermieteten Eigentumswohnung',
    location: 'Rheine',
  },
  {
    quote: 'Schnelle Reaktionszeiten und eine saubere Betriebskostenabrechnung. Die Kommunikation mit Mietern und Handwerkern ist endlich entlastet.',
    author: 'Kapitalanlegerin mit mehreren Einheiten',
    location: 'Münsterland',
  },
  {
    quote: 'Transparente Konditionen und ein fester Ansprechpartner. Die Mandatsübernahme war unkompliziert und professionell umgesetzt.',
    author: 'Eigentümer im Ruhrgebiet',
    location: 'Bochum',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#f4f6fb', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0f172a', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar sx={{ py: 1 }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            <AccountBalanceIcon sx={{ color: '#14b8a6' }} /> immo-automation
          </Typography>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/leistungen/sondereigentumsverwaltung')}>
            SEV-Leistung
          </Button>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/immobilien')}>
            Immobilien
          </Button>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/karriere')}>
            Karriere
          </Button>
          <Button sx={{ color: 'white', textTransform: 'none' }} onClick={() => navigate('/kontakt')}>
            Kontakt
          </Button>
          <Button
            variant="outlined"
            sx={{ ml: 1, borderColor: '#14b8a6', color: '#d1fae5', textTransform: 'none', fontWeight: 700 }}
            onClick={() => navigate('/login')}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          background: 'linear-gradient(135deg, #020817 0%, #0b2545 40%, #0d9488 100%)',
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 14, md: 18 },
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: { xs: 110, md: 160 },
            background: 'linear-gradient(to bottom, rgba(244,246,251,0) 0%, rgba(244,246,251,0.6) 65%, #f4f6fb 100%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ letterSpacing: 1.3, fontWeight: 700, color: '#d1fae5' }}>
                REGIONALE VERWALTUNG UND VERKAUF
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  mt: 1.5,
                  mb: 2,
                  lineHeight: 1.12,
                  color: '#f8fbff',
                  textShadow: '0 3px 20px rgba(2,6,23,0.7)',
                }}
              >
                Sondereigentumsverwaltung im Rheinland,
                <br />
                Ruhrgebiet und Münsterland.
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#eef5ff',
                  maxWidth: 760,
                  fontWeight: 500,
                  lineHeight: 1.55,
                  textShadow: '0 2px 10px rgba(2,6,23,0.5)',
                }}
              >
                Sie entscheiden, wie tief Sie einsteigen: kostenfreier POC zur Selbstverwaltung, klassische Verwaltung als Service
                oder gezielte Vermarktung beim Verkauf Ihrer Immobilie.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button variant="contained" onClick={() => navigate('/leistungen/sondereigentumsverwaltung')} sx={{ bgcolor: '#14b8a6', textTransform: 'none', fontWeight: 700, px: 3 }}>
                  SEV-Angebot berechnen
                </Button>
                <Button variant="outlined" onClick={() => navigate('/immobilien')} sx={{ borderColor: 'white', color: 'white', textTransform: 'none', fontWeight: 700 }}>
                  Immobilienangebote
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -7, md: -10 }, pb: 8, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={2.5}>
          {entryTabs.map((tab) => (
            <Grid item xs={12} md={4} key={tab.title}>
              <Card sx={{ borderRadius: 3, height: '100%', border: '1px solid #dbe3ee', boxShadow: '0 14px 36px rgba(15,23,42,0.08)' }}>
                <CardContent>
                  <Stack spacing={1.2}>
                    <Box sx={{ color: '#0d9488' }}>{tab.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>{tab.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#475569', minHeight: 52 }}>{tab.description}</Typography>
                    <Button variant="contained" sx={{ width: 'fit-content', textTransform: 'none', fontWeight: 700 }} onClick={() => navigate(tab.route)}>
                      {tab.cta}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3, border: '1px solid #dbe3ee' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                  Sondereigentumsverwaltung (SEV)
                </Typography>
                <Typography variant="body1" sx={{ color: '#475569', mb: 2.5 }}>
                  Die SEV richtet sich an Eigentümer einzelner Wohnungen innerhalb größerer Anlagen. Sie entlastet bei
                  Mietverwaltung, Instandhaltung und Mieterkommunikation.
                </Typography>
                <Stack spacing={1}>
                  {sevHighlights.map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#334155' }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Button variant="outlined" onClick={() => navigate('/leistungen/sondereigentumsverwaltung')} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Zur SEV-Leistungsseite
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3, border: '1px solid #dbe3ee', height: '100%' }}>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                  Verkauf mit Exposé-Interaktion
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
                  Kaufinteressenten hinterlassen Kontaktdaten, bestätigen Maklerbedingungen und erhalten anschließend
                  den Exposé-Zugang. Danach folgt strukturiert die Besichtigungsanfrage.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/immobilien')} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Zu den Kaufangeboten
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="overline" sx={{ color: '#0d9488', letterSpacing: 1.2, fontWeight: 700 }}>
            REFERENZEN
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Das sagen Eigentümer über unsere SEV
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {sevReferences.map((ref) => (
            <Grid item xs={12} md={4} key={ref.author}>
              <Card sx={{ borderRadius: 3, height: '100%', border: '1px solid #dbe3ee', boxShadow: '0 8px 24px rgba(15,23,42,0.06)' }}>
                <CardContent>
                  <Stack spacing={1.5} sx={{ height: '100%' }}>
                    <FormatQuoteIcon sx={{ color: '#14b8a6', fontSize: 36 }} />
                    <Typography variant="body1" sx={{ color: '#334155', fontStyle: 'italic', lineHeight: 1.6 }}>
                      „{ref.quote}“
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>{ref.author}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{ref.location}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: '#0f172a', color: '#cbd5e1', py: 5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography sx={{ fontWeight: 700, color: 'white' }}>immo-automation GmbH</Typography>
              <Typography variant="body2">Brechtestraße 64, 48431 Rheine, Deutschland</Typography>
              <Typography variant="body2">Telefon: +49 152 22096381</Typography>
            </Grid>
            <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button sx={{ color: '#cbd5e1', textTransform: 'none' }} onClick={() => navigate('/impressum')}>Impressum</Button>
              <Button sx={{ color: '#cbd5e1', textTransform: 'none' }} onClick={() => navigate('/datenschutz')}>Datenschutz</Button>
              <Button sx={{ color: '#cbd5e1', textTransform: 'none' }} onClick={() => navigate('/karriere')}>Karriere</Button>
              <Button sx={{ color: '#cbd5e1', textTransform: 'none' }} onClick={() => navigate('/kontakt')}>Kontakt</Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
